import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, BookOpen, X } from "lucide-react";
import BookCard from "../components/BookCard";
import useSEO from "../hooks/useSEO";

const SORT_OPTIONS = [
  { value: "default",   label: "Relevance" },
  { value: "downloads", label: "Most Popular" },
  { value: "rating",    label: "Top Rated" },
  { value: "year-desc", label: "Newest First" },
  { value: "year-asc",  label: "Oldest First" },
  { value: "alpha",     label: "A → Z" },
];

export default function SearchPage({ books, loading }) {
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search).get("q") || "";
  const [selectedGenre, setSelectedGenre] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [localQuery, setLocalQuery] = useState(query);
  const [showFilters, setShowFilters] = useState(false);

  const genres = useMemo(() => {
    if (!books) return [];
    return Array.from(new Set(books.map((b) => b.genre).filter(Boolean))).sort();
  }, [books]);

  const filtered = useMemo(() => {
    let list = books || [];
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((b) =>
        (b.title||"").toLowerCase().includes(q) ||
        (b.author||"").toLowerCase().includes(q) ||
        (b.genre||"").toLowerCase().includes(q)
      );
    }
    if (selectedGenre) list = list.filter((b) => b.genre === selectedGenre);
    if (sortBy === "downloads") list = [...list].sort((a,b) => (b.downloadCount||0)-(a.downloadCount||0));
    if (sortBy === "rating")    list = [...list].sort((a,b) => (b.rating||0)-(a.rating||0));
    if (sortBy === "year-desc") list = [...list].sort((a,b) => (b.year||0)-(a.year||0));
    if (sortBy === "year-asc")  list = [...list].sort((a,b) => (a.year||0)-(b.year||0));
    if (sortBy === "alpha")     list = [...list].sort((a,b) => (a.title||"").localeCompare(b.title||""));
    return list;
  }, [books, query, selectedGenre, sortBy]);

  useSEO({
    title: query ? `"${query}" — Ebenezer Books` : "Browse Library — Ebenezer Books",
    description: `Search results for "${query}" on Ebenezer Books.`,
    keywords: "search christian books, christian library, read online",
  });

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(localQuery.trim())}`);
  };

  const clearFilter = () => { setSelectedGenre(""); setSortBy("default"); };
  const hasFilter = selectedGenre || sortBy !== "default";

  return (
    <>
      {/* ── HEADER ── */}
      <section style={{ background:"var(--hero-bg)",position:"relative",overflow:"hidden" }}>
        <div style={{ position:"absolute",inset:0,backgroundImage:"radial-gradient(rgba(255,255,255,0.025) 1px,transparent 1px)",backgroundSize:"32px 32px",pointerEvents:"none" }} />
        <div style={{ maxWidth:"var(--max-w)",margin:"0 auto",padding:"3rem 2rem 2.5rem",position:"relative",zIndex:1 }}>
          <div style={{ fontSize:"0.72rem",color:"var(--gold-light)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"0.5rem" }}>
            Library Search
          </div>
          <h1 style={{ fontFamily:"var(--font-display)",color:"var(--hero-text)",fontSize:"clamp(1.8rem,3.5vw,2.6rem)",fontWeight:400,marginBottom:"0.5rem" }}>
            {query ? <>Results for <em style={{ color:"var(--gold-light)",fontStyle:"italic" }}>"{query}"</em></> : "Browse the Full Library"}
          </h1>
          <p style={{ color:"var(--hero-muted)",fontSize:"0.88rem",marginBottom:"1.75rem" }}>
            {loading ? "Searching…" : `${filtered.length} book${filtered.length!==1?"s":""} found`}
          </p>

          {/* Search bar */}
          <form onSubmit={handleSearch} style={{ display:"flex",gap:"10px",maxWidth:"600px" }}>
            <div style={{
              flex:1,display:"flex",alignItems:"center",gap:"10px",
              background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",
              borderRadius:"var(--radius-full)",padding:"0 16px",
              backdropFilter:"blur(8px)",
            }}>
              <Search size={16} color="rgba(255,255,255,0.5)" style={{ flexShrink:0 }} />
              <input
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder="Title, author, genre…"
                style={{
                  flex:1,border:"none",background:"none",outline:"none",
                  color:"#fff",fontSize:"0.9rem",fontFamily:"var(--font-body)",
                  padding:"11px 0",
                }}
              />
              {localQuery && (
                <button type="button" onClick={() => setLocalQuery("")} style={{ background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",display:"flex",alignItems:"center" }}>
                  <X size={14} />
                </button>
              )}
            </div>
            <button type="submit" style={{
              padding:"0 24px",borderRadius:"var(--radius-full)",border:"none",
              background:"linear-gradient(135deg,var(--gold),var(--gold-2))",
              color:"#fff",fontWeight:700,fontSize:"0.88rem",cursor:"pointer",
              boxShadow:"var(--shadow-gold)",whiteSpace:"nowrap",
            }}>Search</button>
          </form>
        </div>
      </section>

      {/* ── FILTER BAR ── */}
      <div style={{ background:"var(--card-bg)",borderBottom:"1px solid var(--card-border)",position:"sticky",top:"var(--nav-h)",zIndex:50 }}>
        <div style={{ maxWidth:"var(--max-w)",margin:"0 auto",padding:"0.65rem 2rem",display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap" }}>
          <button onClick={() => setShowFilters(!showFilters)} style={{
            display:"flex",alignItems:"center",gap:"6px",padding:"6px 14px",
            borderRadius:"var(--radius-full)",border:"1px solid var(--dust)",
            background: showFilters ? "var(--gold-faint-2)" : "none",
            color: showFilters ? "var(--gold)" : "var(--muted)",
            fontSize:"0.78rem",fontWeight:600,cursor:"pointer",transition:"all 0.15s",flexShrink:0,
          }}>
            <SlidersHorizontal size={13} /> Filters {hasFilter && <span style={{ background:"var(--gold)",color:"#fff",borderRadius:"var(--radius-full)",width:"16px",height:"16px",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:"0.6rem",fontWeight:700 }}>!</span>}
          </button>

          {/* Genre pills */}
          <div className="no-scrollbar" style={{ display:"flex",gap:"6px",overflowX:"auto",flex:1 }}>
            <button onClick={() => setSelectedGenre("")} style={{
              padding:"5px 14px",borderRadius:"var(--radius-full)",border:"none",
              background: !selectedGenre ? "var(--gold)" : "var(--cream-2)",
              color: !selectedGenre ? "#fff" : "var(--muted)",
              fontSize:"0.76rem",fontWeight:!selectedGenre?700:500,cursor:"pointer",transition:"all 0.15s",flexShrink:0,
            }}>All</button>
            {genres.map((g) => (
              <button key={g} onClick={() => setSelectedGenre(selectedGenre===g?"":g)} style={{
                padding:"5px 14px",borderRadius:"var(--radius-full)",border:"none",
                background: selectedGenre===g ? "var(--gold)" : "var(--cream-2)",
                color: selectedGenre===g ? "#fff" : "var(--muted)",
                fontSize:"0.76rem",fontWeight:selectedGenre===g?700:500,cursor:"pointer",transition:"all 0.15s",flexShrink:0,
              }}>{g}</button>
            ))}
          </div>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{
            padding:"6px 12px",borderRadius:"var(--radius)",border:"1px solid var(--dust)",
            background:"var(--input-bg)",color:"var(--ink)",fontSize:"0.76rem",outline:"none",cursor:"pointer",flexShrink:0,
          }}>
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          {hasFilter && (
            <button onClick={clearFilter} style={{
              display:"flex",alignItems:"center",gap:"4px",
              padding:"5px 12px",borderRadius:"var(--radius-full)",border:"none",
              background:"var(--rust-faint)",color:"var(--rust)",fontSize:"0.76rem",fontWeight:600,cursor:"pointer",flexShrink:0,
            }}>
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* ── RESULTS ── */}
      <main style={{ maxWidth:"var(--max-w)",margin:"0 auto",padding:"2.5rem 2rem 5rem" }}>
        {loading ? (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(185px,1fr))",gap:"1.25rem" }}>
            {[...Array(8)].map((_,i) => (
              <div key={i} className="skeleton" style={{ height:"290px",borderRadius:"var(--radius-lg)" }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:"center",padding:"6rem 2rem",color:"var(--muted)" }}>
            <BookOpen size={48} style={{ margin:"0 auto 1.25rem",opacity:0.25 }} />
            <h3 style={{ fontFamily:"var(--font-display)",fontSize:"1.5rem",color:"var(--ink)",marginBottom:"0.5rem" }}>No results found</h3>
            <p style={{ fontSize:"0.88rem",marginBottom:"1.5rem" }}>Try a different search term or browse a category.</p>
            <button onClick={() => { setSelectedGenre(""); setSortBy("default"); navigate("/search"); }} style={{
              padding:"9px 22px",borderRadius:"var(--radius-full)",border:"none",
              background:"var(--gold)",color:"#fff",fontWeight:700,cursor:"pointer",
            }}>Browse All Books</button>
          </div>
        ) : (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(185px,1fr))",gap:"1.25rem" }}>
            {filtered.map((book) => (
              <BookCard key={book.id} book={book} />
            ))}
          </div>
        )}
      </main>
    </>
  );
}
